package co.maytes.standupbot.dashboard.components.layout

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.UserMenu
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideBarChart
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideClock
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideFileText
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideHouse
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideMessagesSquare
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideSettings
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideTag
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideUsers
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberSetupStatusStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.SettingsClasses
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.ZIndex
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.design.StatusHue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onKeyDown
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.tabIndex
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import org.jetbrains.compose.web.attributes.alt
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import kotlin.coroutines.resume

enum class NavGroup { Primary, Admin }

data class NavItem(
    val label: String,
    val path: String,
    val group: NavGroup = NavGroup.Primary,
    val adminOnly: Boolean = false,
    val icon: @Composable (String) -> Unit,
)

val NavItems = listOf(
    NavItem("Home", "/app") { c -> LucideHouse(strokeColor = c) },
    NavItem("Standups", "/app/standups") { c -> LucideMessagesSquare(strokeColor = c) },
    NavItem("Runs", "/app/runs") { c -> LucideClock(strokeColor = c) },
    NavItem("Releases", "/app/releases") { c -> LucideTag(strokeColor = c) },
    NavItem("Templates", "/app/templates", group = NavGroup.Admin, adminOnly = true) { c -> LucideFileText(strokeColor = c) },
    NavItem("Analytics", "/app/analytics", group = NavGroup.Admin, adminOnly = true) { c -> LucideBarChart(strokeColor = c) },
    NavItem("Summary", "/app/summary", group = NavGroup.Admin, adminOnly = true) { c -> LucideUsers(strokeColor = c) },
    NavItem("Teams", "/app/org-teams", group = NavGroup.Admin, adminOnly = true) { c -> LucideUsers(strokeColor = c) },
    NavItem("Settings", "/app/settings", group = NavGroup.Admin, adminOnly = true) { c -> LucideSettings(strokeColor = c) },
)

private const val SIDEBAR_WIDTH = 248

@Composable
fun SideNav(content: (@Composable () -> Unit)? = null) {
    val auth = rememberAuthStore()
    val ui = rememberUiStore()
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val user = auth.currentUser

    Column(
        modifier = Modifier
            .classNames("sb-desktop-only")
            .width(SIDEBAR_WIDTH.px)
            .padding(top = Spacing.md, leftRight = Spacing.xs, bottom = Spacing.xs)
            .styleModifier {
                property("flex", "0 0 ${SIDEBAR_WIDTH}px")
                property("position", "sticky")
                property("top", "0")
                property("height", "100vh")
                property("z-index", "${ZIndex.nav}")
                property("border-right", "1px solid ${Palette.borderSubtle.cssRgb()}")
            },
    ) {
        SidebarBrand(workspaceName = user?.workspace?.name)
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("flex", "1 1 auto")
                    property("min-height", "0")
                    property("overflow-y", "auto")
                },
        ) {
            if (content != null) content() else NavList()
        }
        if (user != null) {
            Box(modifier = Modifier.fillMaxWidth().padding(top = Spacing.xs)) {
                UserMenu(
                    user = user,
                    isAdmin = auth.isAdmin,
                    sidebar = true,
                    modifier = Modifier.fillMaxWidth(),
                    onSettings = { ctx.router.navigateTo("/app/settings") },
                    onViewOnboarding = { ctx.router.navigateTo("/app/onboarding/welcome") },
                    onSignOut = {
                        scope.launch {
                            auth.signOut()
                            ui.showToast("Signed out", ToastKind.Success)
                            ctx.router.navigateTo("/")
                        }
                    },
                )
            }
        }
    }
}

@Composable
fun SidebarBrand(workspaceName: String?) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(leftRight = Spacing.sm, bottom = Spacing.sm),
    ) {
        Link(
            path = "/app",
            modifier = Modifier.styleModifier {
                property("text-decoration", "none")
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "10px")
            },
        ) {
            Img(
                src = "/logo.svg",
                attrs = Modifier
                    .styleModifier {
                        property("width", "28px")
                        property("height", "28px")
                        property("flex-shrink", "0")
                        property("display", "block")
                    }
                    .toAttrs { alt("StandupBot") },
            )
            SpanText(
                text = "Standup Bot",
                modifier = Modifier
                    .fontSize(Typography.body.size)
                    .fontWeight(600)
                    .color(Palette.textPrimary)
                    .styleModifier { property("letter-spacing", "-0.01em") },
            )
        }
        if (!workspaceName.isNullOrBlank()) {
            SpanText(
                text = workspaceName,
                modifier = Modifier
                    .margin(top = Spacing.xs)
                    .fontSize(Typography.caption.size)
                    .color(Palette.textTertiary)
                    .styleModifier {
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                    },
            )
        }
    }
}

@Composable
fun NavList() {
    val auth = rememberAuthStore()
    val setupAttention = rememberSetupStatusStore().state.needsAttention(auth.isAdmin)
    val currentPath = rememberCurrentPath()
    val visible = NavItems.filter { !it.adminOnly || auth.isAdmin }
    val activePath = visible.firstOrNull { isRouteActive(currentPath, it.path) }?.path
    NavPillHost(activeKey = activePath, layoutKey = visible.size) {
        NavGroup.entries.forEach { group ->
            val items = visible.filter { it.group == group }
            if (items.isEmpty()) return@forEach
            NavGroupHeader(if (group == NavGroup.Admin) "Admin" else "Workspace")
            items.forEach { item ->
                NavRow(
                    item = item,
                    active = item.path == activePath,
                    needsAttention = setupAttention && item.path == SETTINGS_PATH,
                )
            }
        }
    }
}

@Composable
fun NavGroupHeader(label: String) {
    SpanText(
        text = label,
        modifier = Modifier
            .padding(leftRight = Spacing.sm)
            .margin(top = Spacing.lg, bottom = Spacing.xxs)
            .fontSize(Typography.caption.size)
            .color(Palette.textTertiary)
            .styleModifier { property("display", "block") },
    )
}

@Composable
fun NavRow(item: NavItem, active: Boolean, needsAttention: Boolean = false) {
    NavRowLink(
        label = item.label,
        active = active,
        icon = item.icon,
        path = item.path,
        needsAttention = needsAttention,
    )
}

@Composable
fun NavRowLink(
    label: String,
    active: Boolean,
    icon: @Composable (String) -> Unit,
    path: String? = null,
    needsAttention: Boolean = false,
    onClick: (() -> Unit)? = null,
) {
    val textColor = if (active) Palette.textPrimary else Palette.textSecondary
    val rowModifier = (if (active) ActiveNavRowStyle else InactiveNavRowStyle).toModifier()
        .classNames("sb-nav-row", "sb-tap")
        .fillMaxWidth()
        .height(32.px)
        .borderRadius(Radius.md)
        .padding(leftRight = Spacing.sm)
        .margin(bottom = 2.px)
        .color(textColor)
        .attrsModifier {
            if (active) attr("data-pill-active", "true")
            if (active) attr("aria-current", "page")
        }
        .styleModifier {
            property("text-decoration", "none")
            property("display", "flex")
            property("align-items", "center")
            property("gap", "10px")
        }
    if (path != null && onClick == null) {
        Link(path = path, modifier = rowModifier) { NavRowBody(label, active, icon, needsAttention) }
    } else {
        Row(
            modifier = rowModifier
                .cursor(Cursor.Pointer)
                .role("link")
                .tabIndex(0)
                .onClick { onClick?.invoke() }
                .onKeyDown { event ->
                    if (event.key == "Enter" || event.key == " ") {
                        event.preventDefault()
                        onClick?.invoke()
                    }
                },
            verticalAlignment = Alignment.CenterVertically,
        ) { NavRowBody(label, active, icon, needsAttention) }
    }
}

@Composable
private fun NavRowBody(
    label: String,
    active: Boolean,
    icon: @Composable (String) -> Unit,
    needsAttention: Boolean,
) {
    val textColor = if (active) Palette.textPrimary else Palette.textSecondary
    val iconColor = if (active) Palette.textPrimary else Palette.textTertiary
    Box(
        modifier = Modifier
            .width(16.px)
            .styleModifier {
                property("height", "16px")
                property("display", "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("flex-shrink", "0")
            }
            .color(iconColor),
    ) {
        icon("currentColor")
    }
    SpanText(
        text = label,
        modifier = Modifier
            .fontSize(Typography.body.size)
            .fontWeight(400)
            .color(textColor),
    )
    if (needsAttention) AttentionDot()
}

private const val SETTINGS_PATH = "/app/settings"

@Composable
private fun AttentionDot() {
    Box(
        modifier = Modifier
            .width(7.px)
            .height(7.px)
            .backgroundColor(Palette.status(StatusHue.Warning).solid)
            .styleModifier {
                property("border-radius", "50%")
                property("flex-shrink", "0")
                property("margin-left", "auto")
            }
            .attrsModifier {
                attr("role", "img")
                attr("aria-label", "Needs attention")
            },
    )
}

val ActiveNavRowStyle = CssStyle {
    base { Modifier.styleModifier { property("background-color", "transparent") } }
}

val InactiveNavRowStyle = CssStyle {
    base { Modifier.styleModifier { property("background-color", "transparent") } }
    hover { Modifier.backgroundColor(Palette.surfaceSubtle) }
}

private class PillHolder(var element: HTMLElement? = null, var placed: Boolean = false)

@Composable
fun NavPillHost(
    activeKey: Any?,
    layoutKey: Any? = null,
    content: @Composable () -> Unit,
) {
    val holder = remember { PillHolder() }

    Div(
        attrs = Modifier
            .classNames(SettingsClasses.PILL_HOST)
            .fillMaxWidth()
            .toAttrs {
                ref { element ->
                    holder.element = element
                    onDispose { if (holder.element === element) holder.element = null }
                }
            },
    ) {
        Div(attrs = Modifier.classNames(SettingsClasses.PILL).toAttrs { attr("aria-hidden", "true") }) {}
        content()
    }

    LaunchedEffect(activeKey, layoutKey) {
        awaitNextFrame()
        val host = holder.element ?: return@LaunchedEffect
        placePill(host, instant = !holder.placed)
        holder.placed = true
    }

    DisposableEffect(Unit) {
        val onResize: (Event) -> Unit = { holder.element?.let { placePill(it, instant = true) } }
        window.addEventListener("resize", onResize)
        onDispose { window.removeEventListener("resize", onResize) }
    }
}

private fun placePill(host: HTMLElement, instant: Boolean) {
    val pill = host.querySelector(".${SettingsClasses.PILL}") as? HTMLElement ?: return
    val active = host.querySelector("[data-pill-active='true']") as? HTMLElement
    if (active == null) {
        pill.style.opacity = "0"
        return
    }
    if (instant) pill.classList.add(SettingsClasses.PILL_INSTANT)
    pill.style.height = "${active.offsetHeight}px"
    pill.style.transform = "translateY(${active.offsetTop}px)"
    pill.style.opacity = "1"
    if (instant) {
        check(pill.offsetWidth >= 0)
        pill.classList.remove(SettingsClasses.PILL_INSTANT)
    }
}

private suspend fun awaitNextFrame() = suspendCancellableCoroutine { continuation ->
    val handle = window.requestAnimationFrame { continuation.resume(Unit) }
    continuation.invokeOnCancellation { window.cancelAnimationFrame(handle) }
}

@Composable
internal fun rememberCurrentPath(): String {
    var path by remember { mutableStateOf(readPath()) }
    return path.also { path = readPath() }
}

private fun readPath(): String = try {
    window.location.pathname
} catch (_: Throwable) {
    ""
}

private fun isRouteActive(currentPath: String, itemPath: String): Boolean {
    if (itemPath == "/app") return currentPath == "/app" || currentPath == "/app/"
    val itemTail = itemPath.removePrefix("/app/")
    val currentTail = currentPath.removePrefix("/app/")
    val itemTopSegment = itemTail.substringBefore("/")
    val currentTopSegment = currentTail.substringBefore("/")
    return itemTopSegment == currentTopSegment && itemTopSegment.isNotBlank()
}
